/**
 * Сессия сотрудника платформы.
 *
 * Роль приходит с сервера при каждом обновлении: если доступ отключили уже
 * после выдачи токена, экран должен закрыться сам, а не ждать, пока токен
 * протухнет.
 */
import type { AdminMe } from '@allswipes/client';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { api } from './api';

interface State {
  me: AdminMe | null;
  loading: boolean;
  signedIn: boolean;
  /** Может ли сотрудник менять чужие данные. Поддержка — только смотрит. */
  canModerate: boolean;
  canManageStaff: boolean;
  refresh: () => Promise<void>;
  signOut: () => void;
}

const Ctx = createContext<State | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<AdminMe | null>(null);
  const [loading, setLoading] = useState(Boolean(api.session));

  const refresh = useCallback(async () => {
    if (!api.session) {
      setMe(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setMe(await api.adminMe());
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    return api.onSessionChange((token) => {
      if (!token) setMe(null);
      else void refresh();
    });
  }, [refresh]);

  const value = useMemo<State>(() => {
    const role = me?.admin.role;
    return {
      me,
      loading,
      signedIn: Boolean(api.session && me),
      canModerate: role === 'superadmin' || role === 'moderator',
      canManageStaff: role === 'superadmin',
      refresh,
      signOut: () => api.setToken(null),
    };
  }, [me, loading, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAdmin(): State {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAdmin вне SessionProvider');
  return ctx;
}
